import { useState, useEffect, useRef } from "react";
import { productAttributeDtlApi, productApi, attributeApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";

/* ────────────────────────────────────────
   HELPERS
──────────────────────────────────────── */
/**
 * Derive the "kind" of value field to show based on attributeType.
 * Adjust the strings here if your backend uses different type names.
 */
function resolveValueKind(attributeType = "") {
  const t = attributeType.toLowerCase();
  if (t.includes("decimal") || t.includes("float") || t.includes("double") || t.includes("number"))
    return "decimal";
  if (t.includes("int") || t.includes("integer"))
    return "integer";
  return "string"; // default → text
}

/** Display whichever value field is populated. */
function resolveDisplayValue(row) {
  if (row.attributeValueString && row.attributeValueString.trim() !== "")
    return { label: row.attributeValueString, kind: "string" };
  if (row.attributeValueDecimal !== null && row.attributeValueDecimal !== undefined && row.attributeValueDecimal !== 0)
    return { label: String(row.attributeValueDecimal), kind: "decimal" };
  if (row.attributeValueInteger !== null && row.attributeValueInteger !== undefined && row.attributeValueInteger !== 0)
    return { label: String(row.attributeValueInteger), kind: "integer" };
  return { label: "—", kind: null };
}

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Attribute?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Detail Drawer ── */
function ProductAttributeDrawer({ row, products, attributes, onClose, onEdit }) {
  const productName = products.find(p => p.productID === row.productID)?.productName ?? "—";
  const attribute = attributes.find(a => a.attributeID === row.attributeID);
  const attributeName = attribute?.attributeName ?? "—";
  const attributeType = attribute?.attributeType ?? "—";
  const { label: displayVal } = resolveDisplayValue(row);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-pa-drawer" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rv-pa-drawer-header">
          <div className="rv-pa-drawer-avatar">
            {attributeName?.charAt(0).toUpperCase()}
          </div>
          <div className="rv-pa-drawer-title-wrap">
            <h3 className="rv-pa-drawer-name">{attributeName}</h3>
            <span className="rv-pa-type-chip"><TypeIcon /> {attributeType}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-pa-drawer-body">
          <div className="rv-pa-detail-grid">
            <div className="rv-pa-detail-item rv-pa-detail-item--full">
              <span className="rv-vendor-detail-label"><ProductIcon /> Product</span>
              <span className="rv-vendor-detail-val">{productName}</span>
            </div>
            <div className="rv-pa-detail-item">
              <span className="rv-vendor-detail-label"><LayersIcon /> Attribute</span>
              <span className="rv-vendor-detail-val">{attributeName}</span>
            </div>
            <div className="rv-pa-detail-item">
              <span className="rv-vendor-detail-label"><TypeIcon /> Type</span>
              <span className="rv-vendor-detail-val">{attributeType}</span>
            </div>
            <div className="rv-pa-detail-item rv-pa-detail-item--full">
              <span className="rv-vendor-detail-label"><ValueIcon /> Value</span>
              <span className="rv-vendor-detail-val rv-pa-value-display">{displayVal}</span>
            </div>
            {/* Raw breakdown */}
            {row.attributeValueString?.trim() !== "" && row.attributeValueString && (
              <div className="rv-pa-detail-item">
                <span className="rv-vendor-detail-label">Text Value</span>
                <span className="rv-vendor-detail-val">{row.attributeValueString}</span>
              </div>
            )}
            {!!row.attributeValueDecimal && (
              <div className="rv-pa-detail-item">
                <span className="rv-vendor-detail-label">Decimal Value</span>
                <span className="rv-vendor-detail-val">{row.attributeValueDecimal}</span>
              </div>
            )}
            {!!row.attributeValueInteger && (
              <div className="rv-pa-detail-item">
                <span className="rv-vendor-detail-label">Integer Value</span>
                <span className="rv-vendor-detail-val">{row.attributeValueInteger}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="rv-pa-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(row); onClose(); }}>
            <EditIcon /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Form Modal ── */
function ProductAttributeModal({ initial, onSave, onClose, saving, products, attributes }) {
  const isEdit = !!initial?.productAttributeID;

  const [form, setForm] = useState({
    productID: initial?.productID ?? "",
    attributeID: initial?.attributeID ?? "",
    attributeValueString: initial?.attributeValueString ?? "",
    attributeValueDecimal: initial?.attributeValueDecimal != null
      ? String(initial.attributeValueDecimal) : "",
    attributeValueInteger: initial?.attributeValueInteger != null
      ? String(initial.attributeValueInteger) : "",
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  /* Resolve selected attribute's type */
  const selectedAttribute = attributes.find(a => a.attributeID === Number(form.attributeID));
  const valueKind = selectedAttribute ? resolveValueKind(selectedAttribute.attributeType) : null;

  /* Clear other value fields when attributeID changes */
  const handleAttributeChange = (val) => {
    setForm(f => ({
      ...f,
      attributeID: val,
      attributeValueString: "",
      attributeValueDecimal: "",
      attributeValueInteger: "",
    }));
    setErrors(e => ({ ...e, attributeID: "", attributeValue: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.productID) e.productID = "Please select a product.";
    if (!form.attributeID) e.attributeID = "Please select an attribute.";
    if (valueKind === "string" && !form.attributeValueString.trim()) e.attributeValue = "Text value is required.";
    if (valueKind === "decimal" && form.attributeValueDecimal === "") e.attributeValue = "Decimal value is required.";
    if (valueKind === "integer" && form.attributeValueInteger === "") e.attributeValue = "Integer value is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-pa-modal">
        {/* Header */}
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><LayersIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Product Attribute" : "New Product Attribute"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the attribute value below." : "Assign an attribute value to a product."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">

          {/* Product */}
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={products}
              valueKey="productID"
              labelKey="productName"
              value={form.productID}
              onChange={val => set("productID", Number(val))}
              placeholder="Select product…"
              searchPlaceholder="Search products…"
              error={errors.productID}
              pageSize={10}
            />
          </div>

          {/* Attribute */}
          <div className="rv-field">
            <label className="rv-label">Attribute <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={attributes.map(a => ({
                ...a,
                label: a.attributeType ? `${a.attributeName} (${a.attributeType})` : a.attributeName
              }))}
              valueKey="attributeID"
              labelKey="label"
              value={form.attributeID}
              onChange={val => handleAttributeChange(val ? Number(val) : "")}
              placeholder="Select attribute…"
              searchPlaceholder="Search attributes…"
              error={errors.attributeID}
              pageSize={10}
            />
          </div>

          {/* Attribute type hint */}
          {selectedAttribute && (
            <div className="rv-pa-type-hint">
              <TypeIcon />
              <span>
                <strong>{selectedAttribute.attributeName}</strong> expects a{" "}
                <em>{selectedAttribute.attributeType || "text"}</em> value
              </span>
            </div>
          )}

          {/* Value field — shown only after attribute is selected */}
          {valueKind === "string" && (
            <div className="rv-field">
              <label className="rv-label">Text Value <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.attributeValue ? "rv-input--err" : ""}`}
                placeholder="e.g. Rose Gold, Hallmarked…"
                value={form.attributeValueString}
                onChange={e => set("attributeValueString", e.target.value)}
              />
              {errors.attributeValue && <p className="rv-field-err">{errors.attributeValue}</p>}
            </div>
          )}

          {valueKind === "decimal" && (
            <div className="rv-field">
              <label className="rv-label">Decimal Value <span className="rv-required">*</span></label>
              <input
                type="number"
                step="0.0001"
                className={`rv-input ${errors.attributeValue ? "rv-input--err" : ""}`}
                placeholder="e.g. 22.50"
                value={form.attributeValueDecimal}
                onChange={e => set("attributeValueDecimal", e.target.value)}
              />
              {errors.attributeValue && <p className="rv-field-err">{errors.attributeValue}</p>}
            </div>
          )}

          {valueKind === "integer" && (
            <div className="rv-field">
              <label className="rv-label">Integer Value <span className="rv-required">*</span></label>
              <input
                type="number"
                step="1"
                className={`rv-input ${errors.attributeValue ? "rv-input--err" : ""}`}
                placeholder="e.g. 5"
                value={form.attributeValueInteger}
                onChange={e => set("attributeValueInteger", e.target.value)}
              />
              {errors.attributeValue && <p className="rv-field-err">{errors.attributeValue}</p>}
            </div>
          )}

          {/* No attribute selected yet */}
          {!form.attributeID && (
            <div className="rv-pa-value-placeholder">
              <ValueIcon />
              <span>Select an attribute above to enter its value</span>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function ProductAttributePage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterProduct, setFilterProduct] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  /* ── Resizable columns ── */
  // Columns: #, Product, Attribute, Type, Value, Actions
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 200, 180, 130, 180, 100]);

  /* ── Fetch ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [rowData, prodData, attrData] = await Promise.all([
        productAttributeDtlApi.getAll(),
        productApi.getAll(),
        attributeApi.getAll(),
      ]);
      setRows(Array.isArray(rowData) ? rowData : rowData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setAttributes(Array.isArray(attrData) ? attrData : attrData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getProductName = (id) => products.find(p => p.productID === id)?.productName ?? "—";
  const getAttributeName = (id) => attributes.find(a => a.attributeID === id)?.attributeName ?? "—";
  const getAttributeType = (id) => attributes.find(a => a.attributeID === id)?.attributeType ?? "—";

  /* ── Unique products present in rows (for filter dropdown) ── */
  const productOptions = products.filter(p =>
    rows.some(r => r.productID === p.productID)
  );

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.productAttributeID) {
        await productAttributeDtlApi.update(formData);
        showToast("Attribute updated successfully.");
      } else {
        await productAttributeDtlApi.create(formData);
        showToast("Attribute created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await productAttributeDtlApi.remove(confirm.id);
      showToast("Attribute deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    const productName = getProductName(r.productID).toLowerCase();
    const attributeName = getAttributeName(r.attributeID).toLowerCase();
    const { label: val } = resolveDisplayValue(r);
    const matchSearch =
      !q ||
      productName.includes(q) ||
      attributeName.includes(q) ||
      val.toLowerCase().includes(q);
    const matchProduct =
      filterProduct === "all" ||
      String(r.productID) === filterProduct;
    return matchSearch && matchProduct;
  });
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  return (
    <div className="rv-pa-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <ProductAttributeModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          products={products}
          attributes={attributes}
        />
      )}
      {drawer && (
        <ProductAttributeDrawer
          row={drawer}
          products={products}
          attributes={attributes}
          onClose={() => setDrawer(null)}
          onEdit={r => setModal({ data: r })}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-pa-topbar">
        <div className="rv-pa-topbar-left">
          <h2 className="rv-pa-heading">Product Attributes</h2>
          <span className="rv-pa-count">{rows.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> Add Attribute
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-pa-filters">
        <div className="rv-pa-search">
          <SearchIcon />
          <input
            placeholder="Search by product, attribute or value…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-pa-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Product filter dropdown */}
        <SearchableDropdown
          options={[
            { productID: "all", productName: "All Products" },
            ...productOptions.map(p => ({ productID: String(p.productID), productName: p.productName }))
          ]}
          valueKey="productID"
          labelKey="productName"
          value={filterProduct}
          onChange={val => { setFilterProduct(val); setPage(1); }}
          placeholder="All Products"
          searchPlaceholder="Search products…"
          className="rv-pa-product-filter"
          pageSize={10}
        />
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-pa-card">
        {loading ? (
          <div className="rv-pa-empty">
            <div className="rv-loader" />
            <p>Loading product attributes…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-pa-empty">
            <div className="rv-empty-icon"><LayersIcon /></div>
            <p className="rv-empty-title">No attributes found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Assign your first attribute to a product."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> Add Attribute
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-pa-table-wrap">
              <table className="rv-table rv-pa-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Attribute</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((r, i) => {
                    const { label: displayVal, kind } = resolveDisplayValue(r);
                    return (
                      <tr
                        key={r.productAttributeID}
                        className="rv-pa-row"
                        onClick={() => setDrawer(r)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-pa-idx">{pageStart + i}</td>

                        {/* Product */}
                        <td>
                          <div className="rv-pa-product-cell">
                            {/* <div className="rv-pa-avatar-sm">
                            {getProductName(r.productID)?.charAt(0).toUpperCase()}
                          </div> */}
                            <span className="rv-pa-product-name">{getProductName(r.productID)}</span>
                          </div>
                        </td>

                        {/* Attribute */}
                        <td>
                          <span className="rv-pa-attr-name">{getAttributeName(r.attributeID)}</span>
                        </td>

                        {/* Type */}
                        <td>
                          <span className="rv-pa-type-badge">{getAttributeType(r.attributeID)}</span>
                        </td>

                        {/* Value */}
                        <td>
                          {kind === null
                            ? <span className="rv-empty-val">—</span>
                            : <span className={`rv-pa-value-badge rv-pa-value-badge--${kind}`}>
                              {displayVal}
                            </span>
                          }
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="rv-actions" onClick={e => e.stopPropagation()}>
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => setModal({ data: r })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({
                                id: r.productAttributeID,
                                name: `${getProductName(r.productID)} → ${getAttributeName(r.attributeID)}`,
                              })}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalItems={totalItems}
            />
          </>

        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function TypeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></svg>; }
function ValueIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6" /><path d="M9 12h6" /><path d="M9 15h4" /></svg>; }